import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import legalTermsMasterService from '../../services/legalTermsMasterService'
import contractTypeMasterService from '../../services/contractTypeMasterService'

const LegalTermsMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const termData = location.state?.termData

  const [formData, setFormData] = useState({
    legalTermsCode: '',
    legalTermsName: '',
    legalTermsDescription: '',
    category: 'General Terms',
    templateContent: '',
    templateVariables: [],
    applicableContractTypes: [],
    version: '1.0',
    complianceRequirements: [],
    jurisdiction: '',
    governingLaw: '',
    status: 'Active',
    notes: ''
  })

  const [contractTypes, setContractTypes] = useState([])
  const [isLoadingLegalTermsCode, setIsLoadingLegalTermsCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newVariable, setNewVariable] = useState({ variableName: '', variableType: 'Text', defaultValue: '', description: '' })
  const [newCompliance, setNewCompliance] = useState('')

  // Fetch next legal terms code (only for new templates)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchLegalTermsCode = async () => {
        try {
          setIsLoadingLegalTermsCode(true)
          const response = await legalTermsMasterService.getNextLegalTermsCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              legalTermsCode: response.data.legalTermsCode || response.data.formattedValue || 'LT0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching legal terms code:', error)
          setFormData(prev => ({ ...prev, legalTermsCode: 'LT0001' }))
        } finally {
          setIsLoadingLegalTermsCode(false)
        }
      }
      fetchLegalTermsCode()
    }
  }, [isEditMode, isViewMode])

  // Load contract types
  useEffect(() => {
    loadContractTypes()
  }, [])

  // Load legal terms data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && termData?._id) {
      loadTermData(termData._id)
    }
  }, [isEditMode, isViewMode, termData])

  const loadContractTypes = async () => {
    try {
      const response = await contractTypeMasterService.getAllContractTypes({ status: 'Active', limit: 1000 })
      if (response.success) {
        setContractTypes(response.data || [])
      }
    } catch (error) {
      console.error('Error loading contract types:', error)
    }
  }

  const loadTermData = async (id) => {
    try {
      setIsLoading(true)
      const response = await legalTermsMasterService.getLegalTermsById(id)
      if (response.success && response.data) {
        const term = response.data
        setFormData({
          legalTermsCode: term.legalTermsCode || '',
          legalTermsName: term.legalTermsName || '',
          legalTermsDescription: term.legalTermsDescription || '',
          category: term.category || 'General Terms',
          templateContent: term.templateContent || '',
          templateVariables: term.templateVariables || [],
          applicableContractTypes: term.applicableContractTypes?.map(ct => ct._id || ct) || [],
          version: term.version || '1.0',
          complianceRequirements: term.complianceRequirements || [],
          jurisdiction: term.jurisdiction || '',
          governingLaw: term.governingLaw || '',
          status: term.status || 'Active',
          notes: term.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading legal terms data:', error)
      toast.error('Failed to load legal terms data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'applicableContractTypes') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value)
      setFormData(prev => ({
        ...prev,
        applicableContractTypes: selectedOptions
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addVariable = () => {
    if (newVariable.variableName.trim()) {
      setFormData(prev => ({
        ...prev,
        templateVariables: [...prev.templateVariables, { ...newVariable }]
      }))
      setNewVariable({ variableName: '', variableType: 'Text', defaultValue: '', description: '' })
    }
  }

  const removeVariable = (index) => {
    setFormData(prev => ({
      ...prev,
      templateVariables: prev.templateVariables.filter((_, i) => i !== index)
    }))
  }

  const addCompliance = () => {
    if (newCompliance.trim()) {
      setFormData(prev => ({
        ...prev,
        complianceRequirements: [...prev.complianceRequirements, newCompliance.trim()]
      }))
      setNewCompliance('')
    }
  }

  const removeCompliance = (index) => {
    setFormData(prev => ({
      ...prev,
      complianceRequirements: prev.complianceRequirements.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.legalTermsName || !formData.templateContent) {
        toast.error('Please fill in all required fields (Template Name and Template Content)')
        return
      }

      setIsLoading(true)
      
      const saveData = {
        ...formData,
        versionChanges: isEditMode ? 'Template content updated' : undefined
      }
      
      if (isEditMode && termData?._id) {
        const response = await legalTermsMasterService.updateLegalTerms(termData._id, saveData)
        if (response.success) {
          toast.success('Legal terms template updated successfully!')
          navigate('/contracts/legal-terms-master')
        } else {
          toast.error(response.message || 'Failed to update legal terms template')
        }
      } else {
        const response = await legalTermsMasterService.createLegalTerms(saveData)
        if (response.success) {
          toast.success('Legal terms template created successfully!')
          navigate('/contracts/legal-terms-master')
        } else {
          toast.error(response.message || 'Failed to create legal terms template')
        }
      }
    } catch (error) {
      console.error('Error saving legal terms template:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save legal terms template')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadTermData(termData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        legalTermsName: '',
        legalTermsDescription: '',
        category: 'General Terms',
        templateContent: '',
        templateVariables: [],
        applicableContractTypes: [],
        version: '1.0',
        complianceRequirements: [],
        jurisdiction: '',
        governingLaw: '',
        status: 'Active',
        notes: ''
      }))
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Legal Terms', path: '/contracts/legal-terms-master' },
    { 
      label: isEditMode ? 'Edit Legal Terms Template' : isViewMode ? 'View Legal Terms Template' : 'New Legal Terms Template', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading legal terms template...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Legal Terms Template' : isViewMode ? 'View Legal Terms Template' : 'New Legal Terms Template'}</h1>
          <p>{isEditMode ? 'Update legal terms template' : isViewMode ? 'View legal terms template' : 'Create a new legal terms template'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/legal-terms-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Template Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Legal Terms Code, Template Name, Category, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="legalTermsCode" className="form-label required">
                Template Code
              </label>
              <input
                type="text"
                id="legalTermsCode"
                name="legalTermsCode"
                value={isLoadingLegalTermsCode ? 'Generating...' : formData.legalTermsCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="legalTermsName" className="form-label required">
                Template Name
              </label>
              <input
                type="text"
                id="legalTermsName"
                name="legalTermsName"
                value={formData.legalTermsName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., Standard Service Agreement Terms"
              />
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="General Terms">General Terms</option>
                <option value="Payment Terms">Payment Terms</option>
                <option value="Delivery Terms">Delivery Terms</option>
                <option value="Warranty Terms">Warranty Terms</option>
                <option value="Liability Terms">Liability Terms</option>
                <option value="Confidentiality">Confidentiality</option>
                <option value="Intellectual Property">Intellectual Property</option>
                <option value="Termination">Termination</option>
                <option value="Dispute Resolution">Dispute Resolution</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Draft">Draft</option>
                <option value="Archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Template Description */}
          <div className="form-group">
            <label htmlFor="legalTermsDescription" className="form-label">
              Description
            </label>
            <textarea
              id="legalTermsDescription"
              name="legalTermsDescription"
              value={formData.legalTermsDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Brief description of the template..."
            />
          </div>

          {/* Template Content */}
          <div className="section-header"><h3>Template Content</h3></div>

          <div className="form-group">
            <label htmlFor="templateContent" className="form-label required">
              Template Content
            </label>
            <textarea
              id="templateContent"
              name="templateContent"
              value={formData.templateContent}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="12"
              placeholder="Enter the legal terms and conditions template content here. You can use variables like {COMPANY_NAME}, {CONTRACT_VALUE}, etc."
              required
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
            <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Use variables in curly braces like {'{VARIABLE_NAME}'} for dynamic content replacement
            </small>
          </div>

          {/* Template Variables */}
          <div className="section-header"><h3>Template Variables</h3></div>

          {formData.templateVariables.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.templateVariables.map((variable, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>
                    <strong>{'{' + variable.variableName + '}'}</strong> - {variable.variableType}
                    {variable.description && <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '4px' }}>{variable.description}</div>}
                    {variable.defaultValue && <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)', marginTop: '2px' }}>Default: {variable.defaultValue}</div>}
                  </div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '12px' }}>
                <input
                  type="text"
                  placeholder="Variable Name (e.g., COMPANY_NAME)"
                  value={newVariable.variableName}
                  onChange={(e) => setNewVariable({ ...newVariable, variableName: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                  className="form-input"
                />
                <select
                  value={newVariable.variableType}
                  onChange={(e) => setNewVariable({ ...newVariable, variableType: e.target.value })}
                  className="form-input"
                >
                  <option value="Text">Text</option>
                  <option value="Number">Number</option>
                  <option value="Date">Date</option>
                  <option value="Currency">Currency</option>
                  <option value="Percentage">Percentage</option>
                </select>
                <input
                  type="text"
                  placeholder="Default Value (optional)"
                  value={newVariable.defaultValue}
                  onChange={(e) => setNewVariable({ ...newVariable, defaultValue: e.target.value })}
                  className="form-input"
                />
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newVariable.description}
                  onChange={(e) => setNewVariable({ ...newVariable, description: e.target.value })}
                  className="form-input"
                />
              </div>
              <button
                type="button"
                onClick={addVariable}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Variable
              </button>
            </div>
          )}

          {/* Applicable Contract Types */}
          <div className="form-group">
            <label htmlFor="applicableContractTypes" className="form-label">
              Applicable Contract Types
            </label>
            <select
              id="applicableContractTypes"
              name="applicableContractTypes"
              value={formData.applicableContractTypes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              multiple
              style={{ minHeight: '100px' }}
            >
              {contractTypes.map(type => (
                <option key={type._id} value={type._id}>
                  {type.contractTypeName} ({type.contractTypeCode})
                </option>
              ))}
            </select>
            <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              Hold Ctrl/Cmd to select multiple contract types
            </small>
          </div>

          {/* Legal and Compliance */}
          <div className="section-header"><h3>Legal and Compliance</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="jurisdiction" className="form-label">
                Jurisdiction
              </label>
              <input
                type="text"
                id="jurisdiction"
                name="jurisdiction"
                value={formData.jurisdiction}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., India, United States"
              />
            </div>

            <div className="form-group">
              <label htmlFor="governingLaw" className="form-label">
                Governing Law
              </label>
              <input
                type="text"
                id="governingLaw"
                name="governingLaw"
                value={formData.governingLaw}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="e.g., Laws of India"
              />
            </div>

            <div className="form-group">
              <label htmlFor="version" className="form-label">
                Version
              </label>
              <input
                type="text"
                id="version"
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                disabled={isViewMode || isEditMode}
                className="form-input"
                style={isEditMode ? { backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' } : {}}
              />
            </div>
          </div>

          {/* Compliance Requirements */}
          {formData.complianceRequirements.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label className="form-label">Compliance Requirements</label>
              {formData.complianceRequirements.map((req, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginBottom: '8px',
                  padding: '12px',
                  background: '#f8f9fa',
                  borderRadius: '6px',
                  alignItems: 'center'
                }}>
                  <div style={{ flex: 1 }}>{req}</div>
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => removeCompliance(index)}
                      style={{
                        padding: '6px',
                        border: 'none',
                        background: '#dc3545',
                        color: '#fff',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Enter compliance requirement"
                value={newCompliance}
                onChange={(e) => setNewCompliance(e.target.value)}
                className="form-input"
                style={{ flex: 1 }}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCompliance())}
              />
              <button
                type="button"
                onClick={addCompliance}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add
              </button>
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default LegalTermsMasterForm

